import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import AdminLayout from './AdminLayout';
import './ApplicationsList.css';

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (d) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const STATUS_STYLES = {
  pending:   { bg: '#fef3c7', color: '#92400e', label: '\u23f3 Pending'    },
  approved:  { bg: '#ffedd5', color: '#9a3412', label: '\ud83d\udd52 Processing' },
  completed: { bg: '#dcfce7', color: '#166534', label: '\u2705 Completed'  },
  rejected:  { bg: '#fee2e2', color: '#7f1d1d', label: '\u2716 Rejected'   },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  return (
    <span style={{
      display: 'inline-block', padding: '3px 12px', borderRadius: 12,
      fontSize: '0.75rem', fontWeight: 700, background: s.bg, color: s.color,
    }}>
      {s.label}
    </span>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function AdminRefunds() {
  const [refunds, setRefunds]             = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [filter, setFilter]               = useState('all');
  const [actionLoading, setActionLoading] = useState(null);
  const [expanded, setExpanded]           = useState(null);
  const [notesDraft, setNotesDraft]       = useState({});
  const [bankVisible, setBankVisible]     = useState({});

  // ── Approve modal ─────────────────────────────────────────────────────────
  const [approveModal, setApproveModal]     = useState(null); // { refund }
  const [approveData, setApproveData]       = useState({ studentMessage: '', refundAmount: '', estimatedHours: '24-48' });
  const [approveLoading, setApproveLoading] = useState(false);

  // ── Complete modal ────────────────────────────────────────────────────────
  const [completeModal, setCompleteModal]     = useState(null); // { refund }
  const [proofFile, setProofFile]             = useState(null);
  const [completionNote, setCompletionNote]   = useState('');
  const [completeLoading, setCompleteLoading] = useState(false);

  const fetchRefunds = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.get('/admin/refunds');
      setRefunds(Array.isArray(data) ? data : data.refunds || []);
      setError(null);
    } catch {
      setError('Failed to load refund requests.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRefunds(); }, [fetchRefunds]);

  // ── Save internal notes only ───────────────────────────────────────────────
  const saveNotes = async (id) => {
    setActionLoading(`${id}-notes`);
    try {
      const r = refunds.find(x => x.id === id);
      await api.patch(`/admin/refunds/${id}`, {
        status: r.status,
        admin_notes: notesDraft[id] ?? r.admin_notes ?? '',
      });
      await fetchRefunds();
      setNotesDraft(prev => { const n = { ...prev }; delete n[id]; return n; });
    } catch {
      alert('Failed to save notes.');
    } finally {
      setActionLoading(null);
    }
  };

  // ── Reject ────────────────────────────────────────────────────────────────
  const handleReject = async (id) => {
    if (!window.confirm('Reject this refund request?')) return;
    setActionLoading(`${id}-rejected`);
    try {
      const r = refunds.find(x => x.id === id);
      await api.patch(`/admin/refunds/${id}`, {
        status: 'rejected',
        admin_notes: notesDraft[id] ?? r?.admin_notes ?? '',
      });
      await fetchRefunds();
    } catch {
      alert('Failed to reject refund.');
    } finally {
      setActionLoading(null);
    }
  };

  // ── Step 1: Approve ───────────────────────────────────────────────────────
  const openApproveModal = (refund) => {
    setApproveData({ studentMessage: '', refundAmount: '', estimatedHours: '24-48' });
    setApproveModal({ refund });
  };

  const handleApproveSubmit = async () => {
    if (!approveData.studentMessage.trim()) {
      alert('Please enter a message to the student explaining the refund details.');
      return;
    }
    const r = approveModal.refund;
    setApproveLoading(true);
    try {
      await api.patch(`/admin/refunds/${r.id}`, {
        status: 'approved',
        admin_notes:         notesDraft[r.id] ?? r.admin_notes ?? '',
        student_message:     approveData.studentMessage,
        refund_amount:       approveData.refundAmount   || null,
        estimated_hours:     approveData.estimatedHours,
        send_approval_email: true,
      });
      setApproveModal(null);
      await fetchRefunds();
    } catch {
      alert('Failed to approve refund. Please try again.');
    } finally {
      setApproveLoading(false);
    }
  };

  // ── Step 2: Complete (upload proof) ───────────────────────────────────────
  const openCompleteModal = (refund) => {
    setProofFile(null);
    setCompletionNote('');
    setCompleteModal({ refund });
  };

  const handleCompleteSubmit = async () => {
    if (!proofFile) {
      alert('Please upload the transaction slip / proof of transfer before completing.');
      return;
    }
    const r = completeModal.refund;
    setCompleteLoading(true);
    try {
      const fd = new FormData();
      fd.append('proof', proofFile);
      fd.append('completion_note', completionNote);
      await api.post(`/admin/refunds/${r.id}/complete`, fd);
      setCompleteModal(null);
      await fetchRefunds();
    } catch {
      alert('Failed to complete refund. Please try again.');
    } finally {
      setCompleteLoading(false);
    }
  };

  // ── Counts ────────────────────────────────────────────────────────────────
  const counts = {
    all:       refunds.length,
    pending:   refunds.filter(r => r.status === 'pending').length,
    approved:  refunds.filter(r => r.status === 'approved').length,
    completed: refunds.filter(r => r.status === 'completed').length,
    rejected:  refunds.filter(r => r.status === 'rejected').length,
  };

  const displayed = filter === 'all' ? refunds : refunds.filter(r => r.status === filter);
  const toggleBank = (id) => setBankVisible(prev => ({ ...prev, [id]: !prev[id] }));

  const cardBorder = (status) => {
    if (status === 'pending')   return '2px solid #f59e0b';
    if (status === 'approved')  return '2px solid #f97316';
    if (status === 'completed') return '2px solid #16a34a';
    return '1px solid #e2e8f0';
  };

  return (
    <AdminLayout pageTitle="Refund Requests">
      <div className="applications-list">

        {/* ── Header ── */}
        <div className="list-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.5rem', color: '#0f172a' }}>Refund Requests</h1>
            <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.875rem' }}>
              {counts.pending} pending &middot; {counts.approved} processing &middot; {counts.completed} completed &middot; {counts.rejected} rejected
            </p>
          </div>
          <button onClick={fetchRefunds} className="btn-view" style={{ fontSize: '0.85rem' }}>&#8635; Refresh</button>
        </div>

        {/* ── Filter Tabs ── */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          {[
            { key: 'all',       label: `All (${counts.all})` },
            { key: 'pending',   label: `Pending (${counts.pending})` },
            { key: 'approved',  label: `Processing (${counts.approved})` },
            { key: 'completed', label: `Completed (${counts.completed})` },
            { key: 'rejected',  label: `Rejected (${counts.rejected})` },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              style={{
                padding: '0.45rem 1rem', borderRadius: 20, border: '1.5px solid',
                borderColor: filter === key ? '#0A1F4E' : '#e2e8f0',
                background:  filter === key ? '#0A1F4E' : '#fff',
                color:       filter === key ? '#fff'    : '#64748b',
                fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* ── Error ── */}
        {error && (
          <div style={{ background: '#fff3f3', border: '1px solid #fca5a5', borderRadius: 8, padding: '12px 16px', color: '#7f1d1d', marginBottom: '1rem', fontWeight: 600 }}>
            {error}
          </div>
        )}

        {/* ── Loading / Empty / List ── */}
        {loading ? (
          <div className="loading-container">
            <div className="spinner-large" />
            <p>Loading refund requests...</p>
          </div>
        ) : displayed.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#94a3b8' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>&#x1F4B7;</div>
            <p style={{ margin: 0, fontWeight: 600 }}>No {filter !== 'all' ? filter : ''} refund requests</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {displayed.map(r => {
              const isExpanded = expanded === r.id;
              const noteDraft  = notesDraft[r.id] ?? r.admin_notes ?? '';
              const bankShow   = bankVisible[r.id];

              return (
                <div
                  key={r.id}
                  style={{
                    background: '#fff',
                    border: cardBorder(r.status),
                    borderRadius: 12,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                    overflow: 'hidden',
                  }}
                >
                  {/* ── Card Header ── */}
                  <div
                    style={{ padding: '1.2rem 1.5rem', cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => setExpanded(isExpanded ? null : r.id)}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                          <strong style={{ fontSize: '1rem', color: '#0f172a' }}>{r.full_name}</strong>
                          <span style={{ fontSize: '0.78rem', fontFamily: 'monospace', background: '#f1f5f9', color: '#475569', padding: '2px 8px', borderRadius: 6 }}>{r.ref_number}</span>
                        </div>
                        <div style={{ display: 'flex', gap: '1rem', marginTop: 4, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '0.82rem', color: '#64748b' }}>{r.email}</span>
                          {r.phone && <span style={{ fontSize: '0.82rem', color: '#64748b' }}>{r.phone}</span>}
                          <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>Submitted: {fmt(r.created_at)}</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                        {r.refund_amount && (
                          <span style={{ fontWeight: 800, fontSize: '1rem', color: '#0f172a' }}>&pound;{Number(r.refund_amount).toFixed(2)}</span>
                        )}
                        <StatusBadge status={r.status} />
                        <span style={{ color: '#94a3b8', fontSize: '0.9rem' }}>{isExpanded ? '▲' : '▼'}</span>
                      </div>
                    </div>
                    <div style={{ marginTop: 8, fontSize: '0.82rem', color: '#64748b', display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                      {r.package_type      && <span>Package: {r.package_type}</span>}
                      {r.first_lesson_date && <span>First lesson: {fmt(r.first_lesson_date)}</span>}
                      {r.lesson_payment_date && (
                        <span style={{ color: '#166534', fontWeight: 600 }}>
                          💳 Paid: {fmt(r.lesson_payment_date)}
                          {r.lesson_payment_amount_pence ? ` — £${(r.lesson_payment_amount_pence / 100).toFixed(2)}` : ''}
                        </span>
                      )}
                      {r.booking_ref       && <span>Ref: {r.booking_ref}</span>}
                      {r.approved_at       && <span>Approved: {fmt(r.approved_at)}</span>}
                      {r.completed_at      && <span>Completed: {fmt(r.completed_at)}</span>}
                    </div>
                  </div>

                  {/* ── Expanded Details ── */}
                  {isExpanded && (
                    <div style={{ borderTop: '1px solid #f1f5f9', padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

                      {/* Reason */}
                      <div>
                        <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#94a3b8', marginBottom: 6 }}>Reason for Refund</div>
                        <div style={{ fontSize: '0.9rem', color: '#1e293b', lineHeight: 1.65, background: '#f8fafc', borderRadius: 8, padding: '10px 14px' }}>{r.reason}</div>
                        {r.additional_notes && (
                          <div style={{ marginTop: 8, fontSize: '0.85rem', color: '#64748b', fontStyle: 'italic', background: '#f8fafc', borderRadius: 8, padding: '8px 14px' }}>
                            <strong>Additional notes:</strong> {r.additional_notes}
                          </div>
                        )}
                      </div>

                      {/* Assigned Instructor */}
                      {r.assigned_instructor_name && (
                        <div>
                          <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#94a3b8', marginBottom: 6 }}>Assigned Instructor</div>
                          <div style={{ fontSize: '0.9rem', color: '#1e293b', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
                            👤 {r.assigned_instructor_name}
                          </div>
                        </div>
                      )}

                      {/* Message that was sent to student on approval */}
                      {r.student_message && (
                        <div>
                          <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#94a3b8', marginBottom: 6 }}>Message Sent to Student (on Approval)</div>
                          <div style={{ fontSize: '0.875rem', color: '#1e293b', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', lineHeight: 1.65 }}>{r.student_message}</div>
                        </div>
                      )}

                      {/* Transaction proof (uploaded when completing) */}
                      {r.proof_image_path && (
                        <div>
                          <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#94a3b8', marginBottom: 6 }}>Transaction Proof</div>
                          <a
                            href={`/uploads/${r.proof_image_path.replace(/\\/g, '/').split('uploads/').pop()}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '8px 14px', color: '#16a34a', fontWeight: 700, fontSize: '0.875rem', textDecoration: 'none' }}
                          >
                            View Transaction Slip
                          </a>
                        </div>
                      )}

                      {/* Bank Details */}
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: 6 }}>
                          <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#94a3b8' }}>Bank Details</div>
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleBank(r.id); }}
                            style={{ fontSize: '0.75rem', padding: '2px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', color: '#475569', fontWeight: 600 }}
                          >
                            {bankShow ? 'Hide' : 'Reveal'}
                          </button>
                        </div>
                        {bankShow ? (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                            {[
                              { label: 'Account Holder', value: r.bank_account_name },
                              { label: 'Sort Code',       value: r.bank_sort_code },
                              { label: 'Account Number', value: r.bank_account_number },
                            ].map(({ label, value }) => (
                              <div key={label} style={{ background: '#fafafa', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 14px' }}>
                                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>{label}</div>
                                <div style={{ fontSize: '0.9rem', color: '#0f172a', fontWeight: 600, fontFamily: 'monospace' }}>{value}</div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{ fontSize: '0.82rem', color: '#94a3b8', fontStyle: 'italic', padding: '6px 0' }}>
                            Click "Reveal" to view bank details
                          </div>
                        )}
                      </div>

                      {/* Internal Admin Notes */}
                      <div>
                        <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#94a3b8', marginBottom: 6 }}>Internal Notes (not visible to student)</div>
                        <textarea
                          value={noteDraft}
                          onChange={e => setNotesDraft(prev => ({ ...prev, [r.id]: e.target.value }))}
                          rows={3}
                          placeholder="Internal admin notes..."
                          style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: '0.875rem', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', color: '#1e293b' }}
                        />
                        {r.reviewed_at && (
                          <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 4 }}>
                            Last reviewed: {fmt(r.reviewed_at)}
                          </div>
                        )}
                      </div>

                      {/* ── Action Buttons ── */}
                      <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center', paddingTop: 8, borderTop: '1px solid #f1f5f9' }}>

                        {/* PENDING: Step 1 — Approve or Reject */}
                        {r.status === 'pending' && (
                          <>
                            <button
                              className="btn-approve"
                              onClick={() => openApproveModal(r)}
                            >
                              Step 1: Approve &amp; Notify Student
                            </button>
                            <button
                              className="btn-reject"
                              disabled={!!actionLoading}
                              onClick={() => handleReject(r.id)}
                            >
                              {actionLoading === `${r.id}-rejected` ? 'Rejecting...' : 'Reject'}
                            </button>
                          </>
                        )}

                        {/* APPROVED / PROCESSING: Step 2 — Upload proof or Reject */}
                        {r.status === 'approved' && (
                          <>
                            <span style={{ fontSize: '0.8rem', color: '#9a3412', background: '#ffedd5', border: '1px solid #fed7aa', borderRadius: 6, padding: '4px 10px', fontWeight: 700 }}>
                              Awaiting transfer proof
                            </span>
                            <button
                              onClick={() => openCompleteModal(r)}
                              style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 16px', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer' }}
                            >
                              Step 2: Upload Proof &amp; Complete
                            </button>
                            <button
                              className="btn-reject"
                              disabled={!!actionLoading}
                              title="Cancel this refund"
                              onClick={() => handleReject(r.id)}
                            >
                              {actionLoading === `${r.id}-rejected` ? 'Rejecting...' : 'Reject'}
                            </button>
                          </>
                        )}

                        {/* COMPLETED: confirmed read-only */}
                        {r.status === 'completed' && (
                          <span style={{ fontWeight: 700, color: '#166534', background: '#dcfce7', border: '1px solid #86efac', borderRadius: 6, padding: '5px 12px', fontSize: '0.85rem' }}>
                            Refund complete &mdash; confirmation + proof emailed to student
                          </span>
                        )}

                        {/* REJECTED: re-open option */}
                        {r.status === 'rejected' && (
                          <>
                            <span style={{ fontWeight: 700, color: '#7f1d1d', fontSize: '0.82rem' }}>Rejected</span>
                            <button
                              className="btn-view"
                              disabled={!!actionLoading}
                              onClick={async () => {
                                setActionLoading(`${r.id}-pending`);
                                await api.patch(`/admin/refunds/${r.id}`, { status: 'pending', admin_notes: r.admin_notes || '' }).catch(() => {});
                                await fetchRefunds();
                                setActionLoading(null);
                              }}
                            >
                              {actionLoading === `${r.id}-pending` ? 'Re-opening...' : 'Re-open'}
                            </button>
                          </>
                        )}

                        {/* Save internal notes button */}
                        {notesDraft[r.id] !== undefined && notesDraft[r.id] !== (r.admin_notes ?? '') && (
                          <button
                            className="btn-view"
                            disabled={!!actionLoading}
                            onClick={() => saveNotes(r.id)}
                            style={{ marginLeft: 'auto', background: '#0A1F4E', color: '#fff', border: 'none' }}
                          >
                            {actionLoading === `${r.id}-notes` ? 'Saving...' : 'Save Notes'}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            STEP 1 MODAL — Approve Refund & Send Email to Student
        ═══════════════════════════════════════════════════════════════════════ */}
        {approveModal && (
          <div className="modal-overlay" onClick={() => !approveLoading && setApproveModal(null)}>
            <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 560, width: '95%' }}>
              <div className="modal-header" style={{ borderBottom: '3px solid #f59e0b' }}>
                <h2>Approve Refund &mdash; {approveModal.refund.full_name}</h2>
                <button className="modal-close" onClick={() => !approveLoading && setApproveModal(null)}>&#215;</button>
              </div>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>

                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '12px 16px', fontSize: '0.875rem', color: '#78350f', lineHeight: 1.65 }}>
                  This will <strong>approve the refund and email the student</strong> telling them when to expect the payment. The request moves to <em>Processing</em> status until you upload proof.
                </div>

                <div>
                  <label style={{ display: 'block', fontWeight: 700, fontSize: '0.85rem', color: '#0f172a', marginBottom: 6 }}>
                    Message to student <span style={{ color: '#CF142B' }}>*</span>{' '}
                    <span style={{ fontWeight: 400, color: '#64748b' }}>(included in the approval email)</span>
                  </label>
                  <textarea
                    rows={4}
                    placeholder="e.g. Your refunded amount will be £150.00. This includes the full upfront booking fee paid via our website."
                    value={approveData.studentMessage}
                    onChange={e => setApproveData(d => ({ ...d, studentMessage: e.target.value }))}
                    style={{
                      width: '100%', padding: '10px 14px', borderRadius: 8,
                      border: `1.5px solid ${approveData.studentMessage ? '#e2e8f0' : '#fca5a5'}`,
                      fontSize: '0.875rem', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: 700, fontSize: '0.85rem', color: '#0f172a', marginBottom: 6 }}>
                      Refund Amount (&pound;)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="e.g. 150.00"
                      value={approveData.refundAmount}
                      onChange={e => setApproveData(d => ({ ...d, refundAmount: e.target.value }))}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: '0.875rem', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 700, fontSize: '0.85rem', color: '#0f172a', marginBottom: 6 }}>
                      Estimated Processing Time
                    </label>
                    <select
                      value={approveData.estimatedHours}
                      onChange={e => setApproveData(d => ({ ...d, estimatedHours: e.target.value }))}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: '0.875rem', boxSizing: 'border-box' }}
                    >
                      <option value="2-4">2-4 working hours</option>
                      <option value="24">24 working hours</option>
                      <option value="24-48">24-48 working hours</option>
                      <option value="48-72">48-72 working hours</option>
                    </select>
                  </div>
                </div>
              </div>

              <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #f1f5f9', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button className="btn-back" onClick={() => !approveLoading && setApproveModal(null)} disabled={approveLoading}>
                  Cancel
                </button>
                <button className="btn-approve" onClick={handleApproveSubmit} disabled={approveLoading}>
                  {approveLoading ? 'Approving...' : 'Approve and Notify Student'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            STEP 2 MODAL — Complete Refund (upload proof)
        ═══════════════════════════════════════════════════════════════════════ */}
        {completeModal && (
          <div className="modal-overlay" onClick={() => !completeLoading && setCompleteModal(null)}>
            <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 520, width: '95%' }}>
              <div className="modal-header" style={{ borderBottom: '3px solid #16a34a' }}>
                <h2>Complete Refund &mdash; {completeModal.refund.full_name}</h2>
                <button className="modal-close" onClick={() => !completeLoading && setCompleteModal(null)}>&#215;</button>
              </div>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>

                <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '12px 16px', fontSize: '0.875rem', color: '#166534', lineHeight: 1.65 }}>
                  Upload the <strong>bank transfer receipt / transaction slip</strong>. The system will mark this refund as <em>Completed</em> and email the student with the proof slip attached.
                </div>

                {/* Proof upload drop zone */}
                <div>
                  <label style={{ display: 'block', fontWeight: 700, fontSize: '0.85rem', color: '#0f172a', marginBottom: 6 }}>
                    Transaction Slip / Proof of Transfer <span style={{ color: '#CF142B' }}>*</span>
                  </label>
                  <div
                    onClick={() => document.getElementById('proof-upload-input').click()}
                    style={{
                      border: `2px dashed ${proofFile ? '#16a34a' : '#e2e8f0'}`,
                      borderRadius: 10, padding: '1.5rem', textAlign: 'center', cursor: 'pointer',
                      background: proofFile ? '#f0fdf4' : '#f8fafc', transition: 'all 0.2s',
                    }}
                  >
                    <input
                      id="proof-upload-input"
                      type="file"
                      accept="image/*,.pdf"
                      style={{ display: 'none' }}
                      onChange={e => setProofFile(e.target.files[0] || null)}
                    />
                    {proofFile ? (
                      <div style={{ color: '#16a34a', fontWeight: 700 }}>
                        {proofFile.name}
                        <div style={{ fontSize: '0.78rem', color: '#166534', fontWeight: 400, marginTop: 4 }}>Click to change</div>
                      </div>
                    ) : (
                      <>
                        <div style={{ fontSize: '1.8rem', marginBottom: 6 }}>&#128206;</div>
                        <div style={{ fontWeight: 600, color: '#475569', marginBottom: 4 }}>Click to upload transaction slip</div>
                        <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>JPG, PNG or PDF &middot; max 10 MB</div>
                      </>
                    )}
                  </div>
                </div>

                {/* Completion note */}
                <div>
                  <label style={{ display: 'block', fontWeight: 700, fontSize: '0.85rem', color: '#0f172a', marginBottom: 6 }}>
                    Completion Note{' '}
                    <span style={{ fontWeight: 400, color: '#64748b' }}>(optional &mdash; included in the email to student)</span>
                  </label>
                  <textarea
                    rows={3}
                    placeholder="e.g. Your refund of £150.00 has been sent via bank transfer. Please allow 1-3 working days."
                    value={completionNote}
                    onChange={e => setCompletionNote(e.target.value)}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: '0.875rem', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #f1f5f9', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button className="btn-back" onClick={() => !completeLoading && setCompleteModal(null)} disabled={completeLoading}>
                  Cancel
                </button>
                <button
                  onClick={handleCompleteSubmit}
                  disabled={completeLoading || !proofFile}
                  style={{
                    background: proofFile ? '#16a34a' : '#94a3b8',
                    color: '#fff', border: 'none', borderRadius: 8,
                    padding: '8px 20px', fontWeight: 700, fontSize: '0.9rem',
                    cursor: proofFile ? 'pointer' : 'not-allowed',
                  }}
                >
                  {completeLoading ? 'Completing...' : 'Mark Complete & Email Student'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </AdminLayout>
  );
}
